import { gql, TypedDocumentNode } from "@apollo/client";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import {
  Address,
  EnumOptions,
  FacilityCourseType,
  FacilityOperatingStatus,
  FacilityOperationType,
  FacilityType,
  PhoneNumber
} from "@/types";
import { PHONE_NUMBER_FRAGMENT, ADDRESS_FRAGMENT } from "@/fragments";
import { PageInfo, PaginationVariables } from "@/components/Pagination";

export type FacilitySearchResult = {
  id: string;
  name: string;
  /** CRM facility legacy id (CCMS facility_id); backend resolves ServiceBus universal id */
  legacyId: string | null;
  section: { id: string; name: string; legacyId: string | null } | null;
  mainAddress: Address | null;
  phone: PhoneNumber | null;
  parentCompany: { id: string; name: string } | null;
  highestRankedEmployment: { id: string; person: { id: string; displayName: string } } | null;
  archivedAt: string | null;
};

export type FacilitiesSearchQueryResponse = {
  facilities: { nodes: FacilitySearchResult[]; pageInfo: PageInfo; totalCount: number };
};

export type FacilitiesSearchQueryInput = {
  search?: string;
  postalCodes?: string[];
  sectionIds?: string[];
  chapterIds?: string[];
  excludeArchived?: boolean;
};

export type FacilitiesSearchQueryVariables = PaginationVariables & { input?: FacilitiesSearchQueryInput };

export const FACILITIES_SEARCH_QUERY: TypedDocumentNode<FacilitiesSearchQueryResponse, FacilitiesSearchQueryVariables> =
  gql`
    query facilitiesSearchQuery(
      $first: Int
      $last: Int
      $before: String
      $after: String
      $input: BackofficeCRMFacilitiesInput!
    ) {
      facilities(first: $first, last: $last, before: $before, after: $after, input: $input) {
        nodes {
          id
          name
          legacyId
          section {
            id
            name
            legacyId
          }
          mainAddress {
            ...AddressFragment
          }
          phone {
            ...PhoneNumberFragment
          }
          parentCompany {
            id
            name
          }
          highestRankedEmployment {
            id
            person {
              id
              displayName
            }
          }
          archivedAt
        }
        ...PageInfo
      }
    }
    ${ADDRESS_FRAGMENT}
    ${PHONE_NUMBER_FRAGMENT}
    ${PAGE_INFO_FRAGMENT}
  `;

export type CRMSectionByPostalCodeQueryResponse = {
  crmSectionByPostalCode: { id: string; name: string; legacyId: string | null } | null;
};

export type CRMSectionByPostalCodeQueryVariables = {
  postalCode: string;
};

export const CRM_SECTION_BY_POSTAL_CODE_QUERY: TypedDocumentNode<
  CRMSectionByPostalCodeQueryResponse,
  CRMSectionByPostalCodeQueryVariables
> = gql`
  query CRMSectionByPostalCode($postalCode: String!) {
    crmSectionByPostalCode(postalCode: $postalCode) {
      id
      name
      legacyId
    }
  }
`;

export type SectionChaptersQueryResponse = {
  section: {
    id: string;
    codedChapters: {
      id: string;
      name: string;
      code: string;
    }[];
  };
};

export const SECTION_CHAPTERS_QUERY: TypedDocumentNode<SectionChaptersQueryResponse, { sectionId: string }> = gql`
  query GetSectionChapters($sectionId: ID!) {
    section(id: $sectionId) {
      id
      codedChapters {
        id
        name
        code
      }
    }
  }
`;

export type SectionWithChapters = {
  id: string;
  name: string;
  legacyId?: string;
  codedChapters: {
    id: string;
    name: string;
    code: string;
  }[];
};

export type SectionsWithChaptersQueryResponse = {
  crmSections: {
    nodes: SectionWithChapters[];
  };
};

// Query to fetch all sections with coded chapters for the facilities page chapter filter
// This is used only on the facilities page to load all data upfront
export const GET_SECTIONS_WITH_CHAPTERS: TypedDocumentNode<
  SectionsWithChaptersQueryResponse,
  { input: { [key: string]: any } }
> = gql`
  query GetSectionsWithChapters($input: BackofficeCRMSectionsInput!) {
    crmSections(input: $input) {
      nodes {
        id
        name
        legacyId
        codedChapters {
          id
          name
          code
        }
      }
    }
  }
`;

export type FacilityEnumOptionsQueryResponse = {
  operatingStatusOptions: EnumOptions<FacilityOperatingStatus>;
  facilityTypeOptions: EnumOptions<FacilityType>;
  courseTypeOptions: EnumOptions<FacilityCourseType>;
  operationTypeOptions: EnumOptions<FacilityOperationType>;
};

export const FACILITY_ENUM_OPTIONS_QUERY: TypedDocumentNode<FacilityEnumOptionsQueryResponse> = gql`
  query FacilityEnumOptions {
    operatingStatusOptions: __type(name: "FacilityOperatingStatus") {
      enumValues {
        name
        description
      }
    }
    facilityTypeOptions: __type(name: "FacilityType") {
      enumValues {
        name
        description
      }
    }
    courseTypeOptions: __type(name: "FacilityCourseType") {
      enumValues {
        name
        description
      }
    }
    operationTypeOptions: __type(name: "FacilityOperationType") {
      enumValues {
        name
        description
      }
    }
  }
`;
